import React from 'react';
import { Edit3, Check, X } from 'lucide-react';

const getBaseSymbol = (sym) => {
  if (!sym) return '';
  return sym.replace('USDT', '').replace('USDC', '').toLowerCase();
};

const PositionsPanel = ({
  activeTab,
  setActiveTab,
  portfolio,
  orders,
  livePrices,
  editingSLTP,
  setEditingSLTP,
  editSLVal,
  setEditSLVal,
  editTPVal,
  setEditTPVal,
  startEditingSLTP,
  saveInlineSLTP,
  handleClosePosition,
  handleCancelOrder,
}) => {
  return (
    <section className="positions-section">
      <div className="tab-headers">
        <button 
          className={`tab-hdr-btn ${activeTab === 'positions' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('positions')}
        >
          Positions ({portfolio.holdings?.length || 0})
        </button>
        <button 
          className={`tab-hdr-btn ${activeTab === 'orders' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Open Orders ({orders.filter(o => o.status === 'PENDING').length || 0})
        </button>
        <button 
          className={`tab-hdr-btn ${activeTab === 'history' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Trade History ({orders.filter(o => o.status === 'FILLED').length || 0})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'positions' && (
          <table className="terminal-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Quantity</th>
                <th>Entry Price</th>
                <th>Mark Price</th>
                <th>Unrealized PnL</th>
                <th>Take Profit (TP)</th>
                <th>Stop Loss (SL)</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(!portfolio.holdings || portfolio.holdings.length === 0) ? (
                <tr>
                  <td colSpan="8" className="empty-table">No open positions. Use the panel on the right to buy assets.</td>
                </tr>
              ) : (
                portfolio.holdings.map((holding) => {
                  const coinSym = getBaseSymbol(holding.symbol);
                  const markPrice = livePrices[coinSym] || holding.avgEntryPrice;
                  const cost = holding.quantity * holding.avgEntryPrice;
                  const curVal = holding.quantity * markPrice;
                  const pnlVal = curVal - cost;
                  const pnlPct = (pnlVal / cost) * 100;
                  const isEditing = editingSLTP === holding.coinId;

                  return (
                    <tr key={holding.coinId}>
                      <td><strong>{holding.symbol.toUpperCase()}</strong></td>
                      <td>{holding.quantity?.toFixed(6)}</td>
                      <td>${holding.avgEntryPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>${markPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className={pnlVal >= 0 ? 'positive' : 'negative'}>
                        {pnlVal >= 0 ? '+' : ''}${pnlVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                      </td>
                      <td>
                        {isEditing ? (
                          <input 
                            type="number" 
                            className="table-input" 
                            value={editTPVal} 
                            onChange={(e) => setEditTPVal(e.target.value)} 
                            placeholder="TP Price"
                          />
                        ) : (
                          holding.takeProfit ? `$${holding.takeProfit.toLocaleString()}` : '--'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input 
                            type="number" 
                            className="table-input" 
                            value={editSLVal} 
                            onChange={(e) => setEditSLVal(e.target.value)} 
                            placeholder="SL Price"
                          />
                        ) : (
                          holding.stopLoss ? `$${holding.stopLoss.toLocaleString()}` : '--'
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons-cell">
                          {isEditing ? (
                            <>
                              <button className="icon-btn check-btn" onClick={() => saveInlineSLTP(holding)} title="Save SL/TP">
                                <Check size={14} />
                              </button>
                              <button className="icon-btn cancel-btn" onClick={() => setEditingSLTP(null)} title="Cancel">
                                <X size={14} />
                              </button>
                            </>
                          ) : (
                            <button className="icon-btn edit-btn" onClick={() => startEditingSLTP(holding)} title="Edit Stop Loss / Take Profit">
                              <Edit3 size={14} />
                            </button>
                          )}
                          <button className="close-pos-btn" onClick={() => handleClosePosition(holding)}>
                            Market Close
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'orders' && (
          <table className="terminal-table">
            <thead>
              <tr>
                <th>Side</th>
                <th>Asset</th>
                <th>Type</th>
                <th>Order Price</th>
                <th>Current Price</th>
                <th>Quantity</th>
                <th>Order Value</th>
                <th>Target SL/TP</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.filter(o => o.status === 'PENDING').length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty-table">No open limit orders.</td>
                </tr>
              ) : (
                orders.filter(o => o.status === 'PENDING').map((order) => {
                  const coinSym = getBaseSymbol(order.symbol);
                  const currentPrice = livePrices[coinSym];
                  return (
                    <tr key={order._id}>
                      <td className={order.side === 'BUY' ? 'positive' : 'negative'}>
                        <strong>{order.side}</strong>
                      </td>
                      <td>{order.symbol.replace('USDT', '')}</td>
                      <td>{order.type}</td>
                      <td>${order.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                      <td>{currentPrice ? `$${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : '--'}</td>
                      <td>{order.quantity}</td>
                      <td>${(order.price * order.quantity)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>
                        {order.takeProfit || order.stopLoss ? (
                          <div className="sltp-capsules">
                            {order.takeProfit && <span className="tp-capsule">TP: {order.takeProfit}</span>}
                            {order.stopLoss && <span className="sl-capsule">SL: {order.stopLoss}</span>}
                          </div>
                        ) : '--'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="close-pos-btn secondary-btn" onClick={() => handleCancelOrder(order._id)}>
                          Cancel
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'history' && (
          <table className="terminal-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Side</th>
                <th>Asset</th>
                <th>Fill Price</th>
                <th>Quantity</th>
                <th>Total Value</th>
                <th style={{ textAlign: 'right' }}>Realized PnL</th>
              </tr>
            </thead>
            <tbody>
              {orders.filter(o => o.status === 'FILLED').length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-table">No transaction history.</td>
                </tr>
              ) : (
                orders.filter(o => o.status === 'FILLED').map((order) => {
                  const totalCost = order.price * order.quantity;
                  const isBuy = order.side === 'BUY';

                  return (
                    <tr key={order._id}>
                      <td>{new Date(order.timestamp).toLocaleString()}</td>
                      <td>{order.type}</td>
                      <td className={isBuy ? 'positive' : 'negative'}>
                        {order.side}
                      </td>
                      <td>{order.symbol.replace('USDT', '')}</td>
                      <td>${order.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                      <td>{order.quantity}</td>
                      <td>${totalCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className={!isBuy ? (order.pnl >= 0 ? 'positive' : 'negative') : ''} style={{ textAlign: 'right' }}>
                        {!isBuy ? (
                          <>
                            {order.pnl >= 0 ? '+' : ''}
                            ${order.pnl?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </>
                        ) : '--'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default PositionsPanel;
